import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider } from "@/context/AuthContext";
import { ShortlistProvider } from "@/context/ShortlistContext";
import { PlayerProvider } from "@/context/PlayerContext";
import { ContentProvider } from "@/context/ContentContext";
import TrackerLoader from "@/components/TrackerLoader";

import Layout from "@/components/Layout";
import Home from "@/pages/Home";
import ChildVoices from "@/pages/ChildVoices";
import ChildVoiceLanguage from "@/pages/ChildVoiceLanguage";
import TalentProfile from "@/pages/TalentProfile";
import Casting from "@/pages/Casting";
import AIVoices from "@/pages/AIVoices";
import Studio from "@/pages/Studio";
import Work from "@/pages/Work";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import Shortlist from "@/pages/Shortlist";
import Application from "@/pages/Application";
import Podcast from "@/pages/Podcast";
import Legal from "@/pages/Legal";
import NotFound from "@/pages/NotFound";
import AuthCallback from "@/pages/AuthCallback";

import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminTalents from "@/pages/admin/AdminTalents";
import AdminTalentEdit from "@/pages/admin/AdminTalentEdit";
import AdminLanguages from "@/pages/admin/AdminLanguages";
import AdminWork from "@/pages/admin/AdminWork";
import AdminClients from "@/pages/admin/AdminClients";
import AdminSite from "@/pages/admin/AdminSite";
import AdminContent from "@/pages/admin/AdminContent";
import AdminInbox from "@/pages/admin/AdminInbox";

import { LEGACY_REDIRECTS } from "@/config/legacyRedirects";
import { englishSlugToDutch, talentPath } from "@/config/childVoices";
import { api } from "@/lib/api";

const withLayout = (el) => <Layout>{el}</Layout>;

// /languages/:slug and /child-voices/:slug (old interim URLs) -> /kinderstemmen/:slug
function LanguageRedirect() {
  const { slug } = useParams();
  return <Navigate to={`/kinderstemmen/${englishSlugToDutch(slug)}`} replace />;
}

// Old /voices/:id profile URLs -> canonical /kinderstemmen/{taal}/{naam}
function VoiceRedirect() {
  const { id } = useParams();
  const [to, setTo] = React.useState(null);
  React.useEffect(() => {
    api.get(`/voices/${id}`).then((r) => setTo(talentPath(r.data))).catch(() => setTo("/kinderstemmen"));
  }, [id]);
  if (!to) return null;
  return <Navigate to={to} replace />;
}

function AppRouter() {
  const location = useLocation();
  // Handle OAuth callback before any route/auth check (race-condition safe)
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/" element={withLayout(<Home />)} />
      <Route path="/kinderstemmen" element={withLayout(<ChildVoices />)} />
      <Route path="/kinderstemmen/:slug" element={withLayout(<ChildVoiceLanguage />)} />
      <Route path="/kinderstemmen/:slug/:voiceSlug" element={withLayout(<TalentProfile />)} />
      <Route path="/voices/:id" element={<VoiceRedirect />} />

      {/* Directory relocation + legacy redirects (client-side fallback; true
          301s are handled at the hosting/CDN layer via _redirects/serve.json) */}
      <Route path="/voices" element={<Navigate to="/kinderstemmen" replace />} />
      <Route path="/child-voices" element={<Navigate to="/kinderstemmen" replace />} />
      <Route path="/child-voices/:slug" element={<LanguageRedirect />} />
      <Route path="/languages/:slug" element={<LanguageRedirect />} />
      {Object.entries(LEGACY_REDIRECTS).map(([from, to]) => (
        <Route key={from} path={from} element={<Navigate to={to} replace />} />
      ))}

      <Route path="/casting" element={withLayout(<Casting />)} />
      <Route path="/ai-voices" element={withLayout(<AIVoices />)} />
      <Route path="/studio" element={withLayout(<Studio />)} />
      <Route path="/work" element={withLayout(<Work />)} />
      <Route path="/about" element={withLayout(<About />)} />
      <Route path="/contact" element={withLayout(<Contact />)} />
      <Route path="/shortlist" element={withLayout(<Shortlist />)} />
      <Route path="/aanmelden" element={withLayout(<Application />)} />
      <Route path="/apply" element={<Navigate to="/aanmelden" replace />} />
      <Route path="/podcast" element={withLayout(<Podcast />)} />
      <Route path="/privacy" element={withLayout(<Legal kind="privacy" />)} />
      <Route path="/terms" element={withLayout(<Legal kind="terms" />)} />

      <Route path="/admin" element={<AdminLayout />}>
        <Route index element={<AdminDashboard />} />
        <Route path="talents" element={<AdminTalents />} />
        <Route path="talents/:id" element={<AdminTalentEdit />} />
        <Route path="languages" element={<AdminLanguages />} />
        <Route path="work" element={<AdminWork />} />
        <Route path="clients" element={<AdminClients />} />
        <Route path="site" element={<AdminSite />} />
        <Route path="content" element={<AdminContent />} />
        <Route path="inbox" element={<AdminInbox />} />
      </Route>

      <Route path="*" element={withLayout(<NotFound />)} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <ShortlistProvider>
        <PlayerProvider>
          <ContentProvider>
            <BrowserRouter>
              <TrackerLoader />
              <AppRouter />
              <Toaster position="top-center" richColors />
            </BrowserRouter>
          </ContentProvider>
        </PlayerProvider>
      </ShortlistProvider>
    </AuthProvider>
  );
}

export default App;
